/**
 * Utility functions for media handling (images and videos)
 */

export type MediaType = 'image' | 'video'

/**
 * Checks if a URL points to a video file based on its extension
 * @param url - The media URL to check
 * @returns true if the URL appears to be a video, false otherwise
 */
export const isVideoUrl = (url: string | undefined | null): boolean => {
  if (!url) return false

  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.m4v']
  const lowerUrl = url.toLowerCase()

  // Use endsWith() to check file extension after the last dot to avoid false positives
  // (e.g., 'my-mp4-converter.jpg' should not be detected as a video)
  const lastDotIndex = lowerUrl.lastIndexOf('.')
  if (lastDotIndex === -1) return false
  const extension = lowerUrl.substring(lastDotIndex)
  return videoExtensions.some((ext) => extension === ext)
}

/**
 * Determines if media is a video based on type field or URL detection
 * @param type - The media type ('image' | 'video')
 * @param url - The media URL (fallback for detection)
 * @returns true if the media is a video
 */
export const isVideo = (type?: MediaType, url?: string): boolean => {
  if (type === 'video') return true
  if (type === 'image') return false
  // Fallback to URL detection if type is not provided
  return isVideoUrl(url)
}

/**
 * Gets the MIME type for a video URL based on its extension
 * @param url - The video URL
 * @returns The MIME type string (e.g., 'video/mp4') or 'video/mp4' as default
 */
export const getVideoMimeType = (url: string): string => {
  const lowerUrl = url.toLowerCase()

  // Extract file extension after the last dot to avoid false positives in URL path or query parameters
  const lastDotIndex = lowerUrl.lastIndexOf('.')
  if (lastDotIndex === -1) return 'video/mp4' // Default if no extension found
  const extension = lowerUrl.substring(lastDotIndex)

  if (extension === '.webm') return 'video/webm'
  if (extension === '.ogg') return 'video/ogg'
  if (extension === '.mov') return 'video/quicktime'
  if (extension === '.avi') return 'video/x-msvideo'
  if (extension === '.m4v') return 'video/x-m4v'

  // Default to mp4 (H.264)
  return 'video/mp4'
}
